import { lastDayOfMonth, endOfDay } from 'date-fns';
import cardValidator from 'card-validator';
import { cnpj, cpf } from 'cpf-cnpj-validator';

import { z } from 'zod';
import { PaymentOption, PaymentCardParams } from '@src/core/buyerApiClient';
import { Translate } from '@src/i18n/useTranslate';
import { getParsedCardValidityValues, parseCardValidity } from '@src/common';

export const schema = (t: Translate['i18n'], isForeignBuy: boolean, paymentOption?: PaymentOption) => {
  const requiredMessage = t('direct_link_required_field');

  const requiredStringField = z.string({ required_error: requiredMessage }).nonempty({ message: requiredMessage });

  return z
    .object({
      holder: requiredStringField,
      holderIdentity: z
        .string()
        .optional()
        .refine((identity) => {
          if (isForeignBuy) {
            return true;
          }

          return cpf.isValid(identity ?? '') || cnpj.isValid(identity ?? '');
        }, t('direct_link_card_form_identity_must_be_valid')),
      cardNumber: requiredStringField
        .refine((number) => {
          const cardInfo = cardValidator.number(number);

          if (cardInfo.card === null) {
            return false;
          }
          const isValid = cardInfo.isValid && cardInfo.isPotentiallyValid;

          return isValid;
        }, t('direct_link_card_form_card_number_must_be_valid'))
        .refine((number) => {
          const cardInfo = cardValidator.number(number);

          if (cardInfo.card === null) {
            return false;
          }

          const brand = cardInfo.card.type === 'american-express' ? 'amex' : cardInfo.card?.type;

          return paymentOption?.brands?.map((b) => b.code).includes(brand);
        }, t('direct_link_card_form_card_brand_not_accepted')),
      securityCode: requiredStringField,
      validity: requiredStringField.refine((date) => {
        const d = endOfDay(lastDayOfMonth(parseCardValidity(date)));
        const currentDate = new Date();

        return d.getTime() >= currentDate.getTime();
      }, t('direct_link_card_form_card_expired')),
      installments: z.coerce.number().min(1),
      postalCode: z
        .string()
        .optional()
        .refine((postalCode) => {
          if (isForeignBuy) {
            return true;
          }

          return postalCode && postalCode.replace(/\W/g, '').length === 8;
        }, t('direct_link_card_form_postal_code_must_be_valid')),
    })
    .transform((data): PaymentCardParams => {
      const { validityMonth, validityYear } = getParsedCardValidityValues(data.validity);

      let holderIdentity = null;

      if (!isForeignBuy && data.holderIdentity) {
        holderIdentity = data.holderIdentity!.replace(/\D/g, '');
      }

      return {
        holder: data.holder,
        holderIdentity,
        cardNumber: data.cardNumber.replace(/\s/g, ''),
        securityCode: data.securityCode,
        validityMonth,
        validityYear,
        installments: data.installments,
        postalCode: data.postalCode?.replace(/\W/g, ''),
      };
    });
};
